import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  OnChanges,
  SimpleChanges,
  Inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FaceCompareService } from '../../../../../services/face-compare/face-compare.service';
import { ToastrService } from 'ngx-toastr';
import { FaceCompareLoaderComponent } from '../../face-compare-loader/face-compare-loader.component';

@Component({
  selector: 'app-face-compare-image-comparison-slider',
  imports: [FormsModule, CommonModule, FaceCompareLoaderComponent],
  templateUrl: './face-compare-image-comparison-slider.component.html',
  styleUrl: './face-compare-image-comparison-slider.component.css',
})
export class FaceCompareImageComparisonSliderComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() selectedImages: any[] = [];
  @Input() processedImages: string[] = [];

  private localSelectedImages: any[] = [];
  private localProcessedImages: string[] = [];

  @ViewChild('image1') image1!: ElementRef<HTMLImageElement>;
  @ViewChild('image2') image2!: ElementRef<HTMLImageElement>;

  selectedImage1 = 0;
  selectedImage2 = 1;
  availableImages: any[] = [];

  isDropdown1Open = false;
  isDropdown2Open = false;

  splitPosition = 50;

  image1Zoom = 1.1;
  image2Zoom = 1.1;
  image1PanX = 0;
  image1PanY = 0;
  image2PanX = 0;
  image2PanY = 0;

  isEnhancingImage = false;

  private isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private currentImage = 0;

  private isSplitDragging = false;

  constructor(
    private faceCompareService: FaceCompareService,
    private toastr: ToastrService,
    private dialogRef: MatDialogRef<FaceCompareImageComparisonSliderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.initializeLocalData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedImages'] || changes['processedImages']) {
      this.initializeLocalData();
    }
  }

  private initializeLocalData(): void {
    // Use dialog data if available, otherwise use input properties
    const images = this.data?.selectedImages || this.selectedImages || [];
    const processed = this.data?.processedImages || this.processedImages || [];

    this.availableImages = images;

    this.localSelectedImages = images.map((img: any) => ({
      ...img,
      url: img.url,
    }));
    this.localProcessedImages = [...processed];

    if (this.availableImages.length >= 2) {
      this.selectedImage1 = 0;
      this.selectedImage2 = 1;
    }
  }

  get currentLoadingState(): { isLoading: boolean; loadingText: string } {
    if (this.isEnhancingImage) {
      return { isLoading: true, loadingText: 'Enhancing image...' };
    }
    return { isLoading: false, loadingText: '' };
  }

  ngOnDestroy(): void {}

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (this.isSplitDragging) {
      this.onSplitDrag(event);
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onDocumentMouseUp(event: MouseEvent): void {
    if (this.isSplitDragging) {
      this.endSplitDrag();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown')) {
      this.isDropdown1Open = false;
      this.isDropdown2Open = false;
    }
  }

  getImage(imageIndex: number): string {
    return (
      this.localSelectedImages[imageIndex]?.url ||
      this.localProcessedImages[imageIndex] ||
      ''
    );
  }

  toggleDropdown1(): void {
    this.isDropdown1Open = !this.isDropdown1Open;
    if (this.isDropdown1Open) {
      this.isDropdown2Open = false;
    }
  }

  toggleDropdown2(): void {
    this.isDropdown2Open = !this.isDropdown2Open;
    if (this.isDropdown2Open) {
      this.isDropdown1Open = false;
    }
  }

  selectImage1(index: number): void {
    this.selectedImage1 = index;
    this.isDropdown1Open = false;
  }

  selectImage2(index: number): void {
    this.selectedImage2 = index;
    this.isDropdown2Open = false;
  }

  startPan(event: MouseEvent, imageNumber: number): void {
    if (event.button === 0 && !this.isSplitDragging) {
      this.isPanning = true;
      this.currentImage = imageNumber;
      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onPan(event: MouseEvent, imageNumber: number): void {
    if (
      this.isPanning &&
      this.currentImage === imageNumber &&
      !this.isSplitDragging
    ) {
      const deltaX = event.clientX - this.panStartX;
      const deltaY = event.clientY - this.panStartY;

      if (imageNumber === 1) {
        this.image1PanX += deltaX;
        this.image1PanY += deltaY;
        this.constrainPan(1);
      } else {
        this.image2PanX += deltaX;
        this.image2PanY += deltaY;
        this.constrainPan(2);
      }

      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
      event.preventDefault();
      event.stopPropagation();
    }
  }

  endPan(imageNumber: number): void {
    if (this.isPanning && this.currentImage === imageNumber) {
      this.isPanning = false;
      this.currentImage = 0;
    }
  }

  startSplitDrag(event: MouseEvent): void {
    this.isSplitDragging = true;
    this.isPanning = false;
    event.preventDefault();
    event.stopPropagation();
  }

  onSplitDrag(event: MouseEvent): void {
    if (this.isSplitDragging) {
      const container = document.querySelector(
        '.comparison-area',
      ) as HTMLElement;
      if (container) {
        const rect = container.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const percentage = (x / rect.width) * 100;
        this.splitPosition = Math.max(10, Math.min(90, percentage));
      }
      event.preventDefault();
    }
  }

  endSplitDrag(): void {
    this.isSplitDragging = false;
  }

  onZoomChange(imageNumber: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const zoom = parseFloat(target.value);

    if (imageNumber === 1) {
      this.image1Zoom = zoom;
      if (zoom <= 1) {
        this.image1PanX = 0;
        this.image1PanY = 0;
      } else {
        this.constrainPan(1);
      }
    } else {
      this.image2Zoom = zoom;
      if (zoom <= 1) {
        this.image2PanX = 0;
        this.image2PanY = 0;
      } else {
        this.constrainPan(2);
      }
    }
  }

  onWheel(event: WheelEvent, imageNumber: number): void {
    event.preventDefault();
    const currentZoom = imageNumber === 1 ? this.image1Zoom : this.image2Zoom;
    const newZoom = this.calculateNewZoom(currentZoom, event.deltaY, 0.1);
    this.updateImageZoomLevel(imageNumber, newZoom);
  }

  private calculateNewZoom(
    currentZoom: number,
    deltaY: number,
    zoomStep: number,
  ): number {
    let newZoom: number;

    if (deltaY < 0) {
      newZoom = Math.min(10, currentZoom + zoomStep);
    } else {
      newZoom = Math.max(0.5, currentZoom - zoomStep);
    }

    return Math.round(newZoom * 10) / 10;
  }

  private updateImageZoomLevel(imageNumber: number, newZoom: number): void {
    if (imageNumber === 1) {
      this.image1Zoom = newZoom;
      this.resetPanForZoom(newZoom, imageNumber);
    } else {
      this.image2Zoom = newZoom;
      this.resetPanForZoom(newZoom, imageNumber);
    }
  }

  private resetPanForZoom(newZoom: number, imageNumber: number): void {
    if (newZoom <= 1) {
      if (imageNumber === 1) {
        this.image1PanX = 0;
        this.image1PanY = 0;
      } else {
        this.image2PanX = 0;
        this.image2PanY = 0;
      }
    } else {
      this.constrainPan(imageNumber);
    }
  }

  resetImage(imageNumber: number): void {
    if (imageNumber === 1) {
      this.image1Zoom = 1;
      this.image1PanX = 0;
      this.image1PanY = 0;
    } else {
      this.image2Zoom = 1;
      this.image2PanX = 0;
      this.image2PanY = 0;
    }
  }

  onEnhance(imageNumber: number): void {
    this.enhanceImage(imageNumber);
  }

  enhanceImage(imageNumber: number): void {
    const imageIndex = imageNumber - 1;

    if (!this.localSelectedImages[imageIndex]?.url) {
      this.toastr.error('No image available to enhance');
      return;
    }

    const imageBase64 = this.localSelectedImages[imageIndex].url;

    this.isEnhancingImage = true;
    this.faceCompareService
      .getEnhancedImage(imageBase64)
      .pipe(
        catchError((error) => {
          this.toastr.error('Failed to enhance image');
          return of(null);
        }),
        finalize(() => {
          this.isEnhancingImage = false;
        }),
      )
      .subscribe((response: any) => {
        if (response && response.data) {
          let enhancedImageData = response.data;
          if (!enhancedImageData.startsWith('data:image')) {
            enhancedImageData = `data:image/png;base64,${enhancedImageData}`;
          }

          this.localSelectedImages[imageIndex].url = enhancedImageData;
          this.localProcessedImages[imageIndex] = enhancedImageData;
        }
      });
  }

  private constrainPan(imageNumber: number): void {
    const image =
      imageNumber === 1
        ? this.image1?.nativeElement
        : this.image2?.nativeElement;
    const zoom = imageNumber === 1 ? this.image1Zoom : this.image2Zoom;

    if (!image) return;

    const container = image.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();

    const scaledWidth = imageRect.width * zoom;
    const scaledHeight = imageRect.height * zoom;
    const maxPanX = Math.max(0, (scaledWidth - containerRect.width) / 2);
    const maxPanY = Math.max(0, (scaledHeight - containerRect.height) / 2);

    if (imageNumber === 1) {
      this.image1PanX = Math.max(-maxPanX, Math.min(maxPanX, this.image1PanX));
      this.image1PanY = Math.max(-maxPanY, Math.min(maxPanY, this.image1PanY));
    } else {
      this.image2PanX = Math.max(-maxPanX, Math.min(maxPanX, this.image2PanX));
      this.image2PanY = Math.max(-maxPanY, Math.min(maxPanY, this.image2PanY));
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
